<script setup lang="ts">
import { computed } from 'vue';
import {
  AGENT_NAME_PATENT_FTO,
  AGENT_NAME_TECH_EXPLORATION,
  AGENT_TOP_ROUTE_NAME_PATENT_FTO,
  AGENT_TOP_ROUTE_NAME_TECH_EXPLORATION,
} from '@/constants';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import { FunctionKey } from '@/types';
import { useFunctionAccess } from '@/utils/composables/useFunctionAccess';
import { featureFlags } from '@/featureFlags';

const emit = defineEmits(['dismissAgentsRecommendation']);
const dismiss = () => {
  emit('dismissAgentsRecommendation');
};

const AGENTS: {
  name: string;
  description: string;
  topRouteName: string;
  functionKey: FunctionKey;
}[] = [
  {
    name: AGENT_NAME_TECH_EXPLORATION,
    description: '技術課題の解決策を探索したい方へ',
    topRouteName: AGENT_TOP_ROUTE_NAME_TECH_EXPLORATION,
    functionKey: 'tech_exploration',
  },
  {
    name: AGENT_NAME_PATENT_FTO,
    description: '自身の研究に関連する特許を調査したい方へ',
    topRouteName: AGENT_TOP_ROUTE_NAME_PATENT_FTO,
    functionKey: 'patent_fto',
  },
];

const functionAccess = featureFlags.ANDEV_10131_CAPABILITY
  ? useFunctionAccess()
  : undefined;

// 取得中は利用できるか分からないので、全て出したうえで選べない状態にする
const isPending = computed(() => !!functionAccess?.isLoading.value);

const visibleAgents = computed(() =>
  AGENTS.filter(
    agent =>
      !functionAccess ||
      isPending.value ||
      functionAccess.canUse(agent.functionKey),
  ),
);
</script>

<template>
  <div v-if="visibleAgents.length > 0" class="agents-recommendation">
    <div class="header">
      <div class="c-title-bold--s">他のAIエージェントもお試しください</div>
      <div class="clickable" @click="dismiss">
        <DgrIcon size="small" name="times" class="icon" :keep-fill="false" />
      </div>
    </div>
    <div v-for="agent in visibleAgents" :key="agent.name">
      <component
        :is="isPending ? 'div' : 'router-link'"
        class="agent-header"
        :class="isPending ? 'disabled' : 'clickable'"
        v-bind="
          isPending
            ? { 'aria-disabled': 'true' }
            : { to: { name: agent.topRouteName }, target: '_blank' }
        "
      >
        <div class="c-title-bold--m">{{ agent.name }}</div>
        <DgrIcon
          size="small"
          name="external-link"
          class="icon"
          :keep-fill="false"
        />
      </component>
      <div class="agent-description c-text-regular--s">
        {{ agent.description }}
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.agents-recommendation {
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 16px;
  background: $color-green600;
  color: $color-white;
  border-radius: 4px;
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .agent-description {
    margin-top: 4px;
  }
  .agent-header {
    display: flex;
    justify-content: start;
    align-items: center;
    gap: 4px;
    color: $color-white;
  }
  .icon {
    color: $color-white;
  }
  .clickable {
    cursor: pointer;
  }
  .disabled {
    cursor: default;
    opacity: 0.6;
  }
}
</style>
